"use client";

import * as TabsPrimitive from "@radix-ui/react-tabs";
import * as React from "react";

import { cn } from "@/common/helpers/utils";

function Tabs({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Root>) {
  return <TabsPrimitive.Root data-slot="tabs" className={cn("flex flex-col gap-2", className)} {...props} />;
}

function TabsList({ className, children, ...props }: React.ComponentProps<typeof TabsPrimitive.List>) {
  const listRef = React.useRef<HTMLDivElement>(null);
  const indicatorRef = React.useRef<HTMLDivElement>(null);

  const updateIndicator = React.useCallback(() => {
    const list = listRef.current;
    const indicator = indicatorRef.current;
    if (!list || !indicator) return;
    const activeTrigger = list.querySelector<HTMLElement>('[data-slot="tabs-trigger"][data-state="active"]');
    if (!activeTrigger) {
      indicator.style.opacity = "0";
      return;
    }
    indicator.style.left = `${activeTrigger.offsetLeft}px`;
    indicator.style.width = `${activeTrigger.offsetWidth}px`;
    indicator.style.opacity = "1";
  }, []);

  React.useLayoutEffect(() => {
    updateIndicator();
    const list = listRef.current;
    if (!list) return;

    const mutationObserver = new MutationObserver(updateIndicator);
    mutationObserver.observe(list, {
      attributes: true,
      subtree: true,
      attributeFilter: ["data-state"],
    });
    const resizeObserver = new ResizeObserver(updateIndicator);
    resizeObserver.observe(list);

    return () => {
      mutationObserver.disconnect();
      resizeObserver.disconnect();
    };
  }, [updateIndicator]);

  return (
    <TabsPrimitive.List
      ref={listRef}
      data-slot="tabs-list"
      className={cn("border-border relative flex items-center gap-1 border-b text-sm", className)}
      {...props}
    >
      {children}
      <div
        ref={indicatorRef}
        aria-hidden="true"
        className="bg-primary absolute -bottom-px h-0.5 rounded-full opacity-0 transition-all duration-200 ease-out"
      />
    </TabsPrimitive.List>
  );
}

function TabsTrigger({ className, onMouseDown, ...props }: React.ComponentProps<typeof TabsPrimitive.Trigger>) {
  // Radix focuses the trigger from script on mousedown. Chrome then shows the keyboard focus ring whenever the last
  // input was a key press (e.g. Ctrl+C or typing in a search), so the click focus is marked as not focus-visible.
  const handleMouseDown = (event: React.MouseEvent<HTMLButtonElement>) => {
    onMouseDown?.(event);
    const trigger = event.currentTarget;
    trigger.focus = (options?: FocusOptions) =>
      HTMLElement.prototype.focus.call(trigger, {
        ...options,
        focusVisible: false,
      } as FocusOptions);
    queueMicrotask(() => Reflect.deleteProperty(trigger, "focus"));
  };

  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      onMouseDown={handleMouseDown}
      className={cn(
        "text-muted-foreground data-[state=active]:text-foreground hover:text-foreground cursor-pointer",
        "inline-flex items-center justify-center gap-2 px-3 py-2 font-medium whitespace-nowrap",
        "focus-visible:ring-ring rounded-sm transition-colors duration-200 focus-visible:ring-1 focus-visible:outline-none",
        "disabled:pointer-events-none disabled:opacity-50",
        "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    />
  );
}

function TabsContent({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return <TabsPrimitive.Content data-slot="tabs-content" className={cn("flex-1 outline-none", className)} {...props} />;
}

export { Tabs, TabsContent, TabsList, TabsTrigger };
